import { useCallback, useMemo } from "react"
import { first, sum } from "lodash"

import { useSkipSkuStoreData } from "~/hooks/useSkipSkuStoreData"
import { type IForecasts, type IWeightMap } from "~/stores/sku-store"
import { api } from "~/utils/api"
import { useForecastingChannels } from "~/hooks/useChannels"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { filterDefined, sumNumberArrays } from "~/utils/tsUtils"
import {
  getForecastBetweenWeeks,
  getForecastBetweenWeeksRounded,
  getForecastsForWeeks,
} from "~/utils/forecastUtils"
import { useFiscalWeek } from "./useFiscalWeek"
import { useGetCollections } from "./useCollections"
import { useVariantsByIds } from "./useVariants"

const weightMaps = new WeakMap<object, IWeightMap>()

// One instance, so a channel without forecasts doesn't hand readers a new map on every render.
const EMPTY_CHANNEL_FORECASTS = new Map<string, number[]>()

const skuForecastCaches = new WeakMap<
  object,
  { weights: IWeightMap | undefined; startDate: number; values: Map<string, number> }
>()

// A SKU's forecast over a window is pure in the forecasts, the weights and the week start, so every
// reader of one response shares its answers. Weights are checked apart from the forecasts because
// structural sharing keeps an unchanged forecasts array when only the size split moved.
export function skuForecastCache(
  forecasts: object,
  weights: IWeightMap | undefined,
  startDate: number,
): Map<string, number> {
  let entry = skuForecastCaches.get(forecasts)
  if (!entry || entry.weights !== weights || entry.startDate !== startDate) {
    entry = { weights, startDate, values: new Map() }
    skuForecastCaches.set(forecasts, entry)
  }
  return entry.values
}

// Transform optimized weights format to Map<skuId, { variantId, weight }>, once per cached
// response so every reader shares one map.
function weightsToMap(weights: Record<string, Record<string, number>>): IWeightMap {
  const cached = weightMaps.get(weights)
  if (cached) return cached
  const map = new Map<string, { weight: number; variantId: string }>()
  for (const [variantId, skuWeights] of Object.entries(weights)) {
    for (const [skuId, weight] of Object.entries(skuWeights)) {
      map.set(skuId, { variantId, weight })
    }
  }
  weightMaps.set(weights, map)
  return map
}

// TODO DON'T CREATE HOOKS THAT OTHER HOOKS ALREADY DO, ESPECIALLY IN THIS PART OF THE CODE
// IT IS VERY DIFFICULT TO GIVE ANY GUARANTEES THAT THE DATA IS CORRECT AND TAKE RIGHT THINGS INTO ACCOUNT

export const useForecastMetadata = () => api.forecasts.getForecastMetadata.useQuery()

export const useForecastModels = () => api.forecasts.getForecastModels.useQuery()

function getForecastForVariant(
  variantId: string,
  warehouseIds: string[] | undefined,
  forecasts: IForecasts[],
) {
  if (forecasts?.length === 1) {
    return [forecasts[0]?.forecasts[variantId]]
  } else if (warehouseIds?.length) {
    const forecastList = forecasts
      .filter((f) => warehouseIds.includes(f.warehouseId))
      .map((f) => f.forecasts[variantId])

    const maxLength = forecastList.reduce((max, f) => Math.max(max, f?.length ?? 0), 0)

    const result = Array.from({ length: maxLength }, (_, i) =>
      forecastList.reduce((sum, current) => {
        return sum + (current?.[i] ?? 0)
      }, 0),
    )
    return [result]
  } else {
    return forecasts?.map(({ forecasts }) => forecasts[variantId]) ?? []
  }
}

export function useChannelForecast(
  channel: string | undefined,
  warehouseId: string | undefined = undefined,
  enabled: boolean,
) {
  const {
    data: fullForecastMap,
    isLoading,
    isFetching,
  } = api.forecasts.getChannelForecastMap.useQuery(
    {
      channels: channel ? [channel] : [],
      warehouseId,
    },
    {
      enabled: !!channel && enabled,
    },
  )
  const forecastMap = fullForecastMap
    ? (fullForecastMap?.get("all") ?? EMPTY_CHANNEL_FORECASTS)
    : undefined
  const startDate = getStartOfCurrentWeekUTC().getTime()
  const millisecondsForAWeek = 1000 * 60 * 60 * 24 * 7
  return {
    forecasts: forecastMap,
    getForecastUntilDay: (date: Date, variantId: string) => {
      const weeks = (date.getTime() - startDate) / millisecondsForAWeek
      const forecastValues = forecastMap?.get(variantId)
      if (!forecastValues) return 0
      return getForecastsForWeeks(forecastValues, weeks)
    },
    getForecastBetweenDates: (
      variantId: string,
      firstDate: Date | null,
      secondDate: Date | null,
    ) => {
      const startWeek = firstDate ? (firstDate.getTime() - startDate) / millisecondsForAWeek : 0
      const endWeek = secondDate ? (secondDate.getTime() - startDate) / millisecondsForAWeek : 0
      const forecastValues = forecastMap?.get(variantId)
      if (!forecastValues) return 0
      return getForecastBetweenWeeks(forecastValues, startWeek, endWeek)
    },
    isLoading,
    isFetching,
  }
}

export function useForecasts() {
  const { data: forecasts, isLoading: forecastLoading, isFetching } = useRawForecasts()
  const { data: weights } = useRawForecastWeights()

  const startDate = getStartOfCurrentWeekUTC().getTime()
  const millisecondsForAWeek = 1000 * 60 * 60 * 24 * 7
  const millisecondsForADay = 1000 * 60 * 60 * 24

  function getForecastUntilDay(date: Date, variantId: string, warehouseId: string | undefined) {
    const weeks = (date.getTime() - startDate) / millisecondsForAWeek
    const forecastValues = getForecastForVariant(
      variantId,
      warehouseId ? [warehouseId] : undefined,
      forecasts ?? [],
    )

    if (forecastValues.length === 1 && forecastValues[0]) {
      return getForecastsForWeeks(forecastValues[0] ?? [], weeks)
    } else {
      return sum(filterDefined(forecastValues).map((values) => getForecastsForWeeks(values, weeks)))
    }
  }

  function getForecastsForEveryWarehouse(date: Date, variantId: string) {
    const weeks = (date.getTime() - startDate) / millisecondsForAWeek
    let values: { warehouseId: string; value: number }[] = []
    for (const maps of [forecasts])
      if (maps) {
        values = filterDefined(
          maps.map((obj) => {
            const series = obj.forecasts[variantId]
            if (series) {
              return {
                warehouseId: obj.warehouseId,
                value: getForecastsForWeeks(series, weeks),
              }
            }
          }),
        )
        if (values.length) {
          return values
        }
      }
    return values
  }

  const getForecastsBetweenDates = useCallback(
    (
      variantId: string,
      warehouseIds: string[] | undefined,
      firstDate: Date | null,
      secondDate: Date | null,
    ) => {
      const startWeek = firstDate ? (firstDate.getTime() - startDate) / millisecondsForAWeek : 0
      const endWeek = secondDate
        ? (secondDate.getTime() - startDate + millisecondsForADay) / millisecondsForAWeek
        : (first(forecasts)?.forecasts[variantId]?.length ?? 0) // add one day to include the full last day

      const forecastValues = getForecastForVariant(variantId, warehouseIds, forecasts ?? [])

      if (forecastValues.length === 1 && forecastValues[0]) {
        return getForecastBetweenWeeks(forecastValues[0] ?? [], startWeek, endWeek)
      } else {
        return sum(
          filterDefined(forecastValues).map((values) =>
            getForecastBetweenWeeks(values, startWeek, endWeek),
          ),
        )
      }
    },
    [forecasts],
  )

  const computeSkuForecastsBetweenDates = useCallback(
    (
      skuId: string,
      warehouseIds: string[] | undefined,
      firstDate: Date | null,
      secondDate: Date | null,
    ) => {
      const startWeek = firstDate ? (firstDate.getTime() - startDate) / millisecondsForAWeek : 0
      const endWeek = secondDate ? (secondDate.getTime() - startDate) / millisecondsForAWeek : 0

      // Check for preorder-adjusted SKU forecast first
      const selectedForecasts = warehouseIds?.length
        ? forecasts?.filter((f) => warehouseIds.includes(f.warehouseId))
        : forecasts
      const specialForecastArrays = filterDefined(
        selectedForecasts?.map((f) => f.specialSkuForecasts[skuId]),
      )
      if (specialForecastArrays.length > 0) {
        return sum(
          specialForecastArrays.map((arr) => getForecastBetweenWeeks(arr, startWeek, endWeek)),
        )
      }

      // Fall back to weighted variant forecast
      const weight = weights?.get(skuId)
      if (!weight) return 0
      return (
        getForecastsBetweenDates(weight.variantId, warehouseIds, firstDate, secondDate) *
        weight.weight
      )
    },
    [weights, forecasts, getForecastsBetweenDates],
  )

  const getSkuForecastsBetweenDates = useCallback(
    (
      skuId: string,
      warehouseIds: string[] | undefined,
      firstDate: Date | null,
      secondDate: Date | null,
    ) => {
      if (!forecasts)
        return computeSkuForecastsBetweenDates(skuId, warehouseIds, firstDate, secondDate)
      const cache = skuForecastCache(forecasts, weights, startDate)
      const scope = warehouseIds?.length ? warehouseIds.join("\u0000") : "*"
      const key = `${skuId}|${scope}|${firstDate?.getTime() ?? ""}|${secondDate?.getTime() ?? ""}`
      let value = cache.get(key)
      if (value === undefined) {
        value = computeSkuForecastsBetweenDates(skuId, warehouseIds, firstDate, secondDate)
        cache.set(key, value)
      }
      return value
    },
    [forecasts, weights, startDate, computeSkuForecastsBetweenDates],
  )

  const getForecastArray = useCallback(
    (variantId: string) => {
      return sumNumberArrays(filterDefined(forecasts?.map((f) => f.forecasts[variantId])))
    },
    [forecasts],
  )

  return {
    forecasts,
    getSkuForecastsBetweenDates,
    getForecastUntilDay,
    getForecastsForEveryWarehouse,
    getForecastsBetweenDates,
    warehouseIds: forecasts?.map((f) => f.warehouseId) ?? [],
    isLoading: forecastLoading,
    isFetching,
    getForecastArray,
  }
}

// Observe only: useUpdatedForecasts owns the org-wide fetch, so mounting a reader never refetches it.
function useAllForecasts() {
  const skipSkuStoreData = useSkipSkuStoreData()
  const { data, isFetching, isLoading } = api.forecasts.getAllForecasts.useQuery(undefined, {
    enabled: false,
  })
  return { data: skipSkuStoreData ? undefined : data, isFetching, isLoading }
}

export const useRawForecasts = () => {
  const { data, isFetching, isLoading } = useAllForecasts()
  return { data: data?.forecasts, isFetching, isLoading }
}

export const useRawForecastWeights = () => {
  const { data, isFetching, isLoading } = useAllForecasts()
  return { data: data && weightsToMap(data.weights), isFetching, isLoading }
}

export const useUpdatedForecasts = (enabled: boolean) => {
  api.forecasts.getAllForecasts.useQuery(undefined, { enabled })
}

export function useForecastForCollectionVariants(
  variantIds: string[],
  warehouseId?: string,
  channel?: string,
) {
  const { getForecastBetweenDates: getChannelForecastsBetweenDates } = useChannelForecast(
    channel,
    warehouseId,
    true,
  )
  const { getForecastsBetweenDates } = useForecasts()
  const { data: collections } = useGetCollections()
  const variants = useVariantsByIds(variantIds)
  return variants.flatMap((v) =>
    v.collections.map((vc) => {
      const collection = collections?.find((c) => c.id === Number(vc.id))
      if (!collection) return
      return {
        collectionName: collection.name,
        collectionId: collection.id,
        startDate: collection.startDate,
        endDate: collection.endDate,
        forecasts: channel
          ? getChannelForecastsBetweenDates(v.variantId, collection.startDate, collection.endDate)
          : getForecastsBetweenDates(
              v.variantId,
              warehouseId ? [warehouseId] : undefined,
              collection.startDate,
              collection.endDate,
            ),
      }
    }),
  )
}

export const useForecastsForVariantsByDates = (
  dateRanges: { key: string; startDate: Date; endDate: Date }[],
  variantIds: string[],
  warehouseId?: string,
  channel?: string,
) => {
  const baseDate = getStartOfCurrentWeekUTC().getTime()
  const millisecondsForAWeek = 1000 * 60 * 60 * 24 * 7
  const { data: forecasts } = useRawForecasts()
  const { data: allChannels } = useForecastingChannels()
  const shouldUseChannelLevelForecast = !!channel
  const channels = channel ? [channel] : (allChannels?.map((c) => c.id) ?? [])
  const { data: channelForecastMap, fetchStatus } = api.forecasts.getChannelForecastMap.useQuery(
    {
      channels,
      warehouseId,
    },
    {
      enabled: shouldUseChannelLevelForecast && channels.length > 0,
    },
  )

  const flattenedForecastMap = useMemo(() => {
    if (!channelForecastMap) {
      return undefined
    }

    const result: Record<string, number[]> = {}
    Array.from(channelForecastMap.values()).forEach((entryMap) => {
      Array.from(entryMap.entries()).forEach(([variantId, values]) => {
        result[variantId] = values
      })
    })
    return result
  }, [channelForecastMap])

  const hasChannelForecasts =
    flattenedForecastMap != null && Object.keys(flattenedForecastMap).length > 0
  const rawForecastMaps =
    hasChannelForecasts && shouldUseChannelLevelForecast
      ? [flattenedForecastMap]
      : (forecasts ?? [])
          .filter((f) => !warehouseId || f.warehouseId === warehouseId)
          .map((f) => f.forecasts)

  const ranges = dateRanges
    .map((r) => ({
      key: r.key,
      startWeek: (r.startDate.getTime() - baseDate) / millisecondsForAWeek,
      endWeek: (r.endDate.getTime() - baseDate) / millisecondsForAWeek,
    }))
    .filter((r) => r.endWeek > 0)
  const singleMap = rawForecastMaps.length === 1 ? rawForecastMaps[0] : undefined

  // Fiscal periods are whole weeks: sum rounded weeks so periods match displayed weekly cells.
  // Non-fiscal months split weeks (fractional bounds) and keep raw sums.
  const { isFiscal } = useFiscalWeek()
  function rangeValue(seriesList: number[][], r: { startWeek: number; endWeek: number }) {
    return isFiscal && Number.isInteger(r.startWeek) && Number.isInteger(r.endWeek)
      ? getForecastBetweenWeeksRounded(seriesList, r.startWeek, r.endWeek)
      : sum(seriesList.map((series) => getForecastBetweenWeeks(series, r.startWeek, r.endWeek)))
  }

  function makeForecastObject(variantId: string) {
    const seriesList = singleMap
      ? [singleMap[variantId] ?? []]
      : rawForecastMaps.map((map) => map?.[variantId] ?? [])
    return Object.fromEntries(ranges.map((r) => [r.key, rangeValue(seriesList, r)]))
  }

  // dateRanges.length alone misses changes when count stays the same but dates shift (e.g. ISO week alignment)
  return useMemo(() => {
    return new Map(variantIds.map((variantId) => [variantId, makeForecastObject(variantId)]))
  }, [
    channel,
    fetchStatus,
    dateRanges.length,
    forecasts,
    variantIds,
    warehouseId,
    flattenedForecastMap,
    isFiscal,
  ])
}
